import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang, useMatch } from "@/App";
import { QUESTIONS, COUNTRIES, RATING_LABELS, UI, tr } from "@/i18n";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { track } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Assessment = () => {
  const { lang } = useLang();
  const { setMatchData, setAnswers } = useMatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { track("assessment_started"); }, []);

  const visible = useMemo(
    () => QUESTIONS.filter((q) => !q.showIf || Object.entries(q.showIf).every(([k, v]) => data[k] === v)),
    [data]
  );
  const q = visible[step];
  const pct = Math.round(((step + 1) / visible.length) * 100);

  const setVal = (v) => setData((d) => ({ ...d, [q.id]: v }));
  const setRating = (item, v) => setData((d) => ({ ...d, [q.id]: { ...(d[q.id] || {}), [item]: v } }));
  const toggleMulti = (v) => {
    const cur = data[q.id] || [];
    setVal(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };

  const answered = () => {
    const val = data[q.id];
    if (q.type === "multi") return Array.isArray(val) && val.length > 0;
    if (q.type === "rating") return true;
    return val !== undefined && val !== null && val !== "";
  };

  const submit = async (finalData) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/match`, { answers: finalData, lang });
      setAnswers(finalData);
      setMatchData(res.data);
      track("assessment_completed", { top_match: res.data?.results?.[0]?.code });
      navigate("/results");
    } catch (e) {
      toast.error(tr(lang, UI.error_generic));
      setLoading(false);
    }
  };

  const next = () => {
    if (step < visible.length - 1) {
      setStep((s) => s + 1);
    } else {
      submit(data);
    }
  };
  const back = () => (step > 0 ? setStep((s) => s - 1) : navigate("/"));

  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center gap-6 px-6">
        <div className="w-14 h-14 border-2 border-navy/20 border-t-navy rounded-full animate-spin" />
        <p className="font-display text-2xl text-navy-deep">{tr(lang, UI.analyzing)}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
          <span className="overline text-gold">{tr(lang, q.section)}</span>
          <span>{step + 1} {tr(lang, UI.q_of)} {visible.length}</span>
        </div>
        <Progress value={pct} className="h-1.5" data-testid="assessment-progress" />
      </div>

      <div key={q.id} className="om-fade-up">
        <h2 className="font-display text-3xl sm:text-4xl text-navy-deep leading-tight">{tr(lang, q.title)}</h2>
        {q.subtitle && <p className="mt-3 text-muted-foreground">{tr(lang, q.subtitle)}</p>}

        <div className="mt-8">
          {q.type === "select" && (
            <Select value={data[q.id] || ""} onValueChange={setVal}>
              <SelectTrigger data-testid={`q-${q.id}-select`} className="h-14 text-base rounded-sm">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`opt-${q.id}-${c.value}`}>
                    {tr(lang, c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {(q.type === "single" || q.type === "multi") && (
            <div className="grid sm:grid-cols-2 gap-3">
              {q.options.map((o) => {
                const active = q.type === "multi" ? (data[q.id] || []).includes(o.value) : data[q.id] === o.value;
                return (
                  <button
                    key={o.value}
                    data-testid={`opt-${q.id}-${o.value}`}
                    onClick={() => (q.type === "multi" ? toggleMulti(o.value) : setVal(o.value))}
                    className={`group flex items-center justify-between text-left px-5 py-4 rounded-sm border transition-colors duration-300 ${active ? "border-navy bg-navy text-white" : "border-border bg-white hover:border-navy/50"}`}
                  >
                    <span className="font-medium">{tr(lang, o)}</span>
                    <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${active ? "border-white bg-white" : "border-border"}`}>
                      {active && <Check className="w-3 h-3 text-navy" />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {q.type === "rating" && (
            <div className="space-y-3">
              {q.items.map((item) => {
                const val = (data[q.id] || {})[item.value] ?? 0;
                return (
                  <div key={item.value} className="bg-white border border-border rounded-sm px-4 py-3">
                    <p className="font-medium mb-2.5">{tr(lang, item)}</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {RATING_LABELS.map((rl, idx) => (
                        <button
                          key={idx}
                          data-testid={`rate-${q.id}-${item.value}-${idx}`}
                          onClick={() => setRating(item.value, idx)}
                          className={`text-[11px] sm:text-xs py-2 px-1 rounded-sm border transition-colors duration-300 ${val === idx ? "border-navy bg-navy text-white" : "border-border text-muted-foreground hover:border-navy/40"}`}
                        >
                          {tr(lang, rl)}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button data-testid="assessment-back" onClick={back} className="inline-flex items-center gap-2 text-muted-foreground hover:text-navy transition-colors duration-300 font-medium">
          <ArrowLeft className="w-4 h-4" /> {tr(lang, UI.back)}
        </button>
        <button
          data-testid="assessment-next"
          onClick={next}
          disabled={!answered()}
          className="inline-flex items-center gap-2 bg-navy text-white px-7 py-3.5 rounded-sm font-semibold disabled:opacity-40 hover:bg-navy-deep transition-colors duration-300"
        >
          {step === visible.length - 1 ? tr(lang, UI.see_results) : tr(lang, UI.next)}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Assessment;
